import { useEffect, useRef } from 'react'

// Minor visualViewport changes (e.g. autocomplete bar) are below this threshold.
const KEYBOARD_THRESHOLD = 60
const MOBILE_BREAKPOINT = 768

const isMobile = () => window.innerWidth < MOBILE_BREAKPOINT

const useVirtualKeyboard = () => {
    const initialVVHeight = useRef(0)
    const isKeyboardOpen = useRef(false)

    useEffect(() => {
        const vp = window.visualViewport
        if (!vp) return

        if (vp.height > 0) initialVVHeight.current = vp.height

        const setKeyboardHeight = (px: number) =>
            document.documentElement.style.setProperty('--keyboard-height', `${px}px`)

        // #root takes this height while the keyboard is open (see index.scss): iOS does not shrink the layout
        // viewport (so not 100dvh either), the part of the page behind the keyboard would hide the input area.
        const setAppHeight = (px: number | null) => {
            if (px === null) document.documentElement.style.removeProperty('--app-height')
            else document.documentElement.style.setProperty('--app-height', `${px}px`)
        }

        const scrollToTop = () => window.scrollTo({ top: 0, left: 0, behavior: 'instant' })

        const openKeyboard = () => {
            isKeyboardOpen.current = true
            document.documentElement.classList.add('keyboard-open')
            document.body.style.overflow = 'hidden'
            setKeyboardHeight(initialVVHeight.current - vp.height)
            if (isMobile()) setAppHeight(vp.height)
            scrollToTop()
        }

        const closeKeyboard = () => {
            isKeyboardOpen.current = false
            document.documentElement.classList.remove('keyboard-open')
            document.body.style.overflow = ''
            setKeyboardHeight(0)
            setAppHeight(null)
            scrollToTop()
            // Re-snapshot so orientation changes don't stale the baseline.
            if (vp.height > 0) initialVVHeight.current = vp.height
        }

        const handleResize = () => {
            if (initialVVHeight.current - vp.height > KEYBOARD_THRESHOLD) {
                openKeyboard()
            } else {
                closeKeyboard()
            }
        }

        // iOS auto-scrolls the page to bring the focused textarea into view after
        // the keyboard opens — this happens after the resize event fires, so our
        // scrollTo(0,0) in openKeyboard can be overridden. Listening to
        // visualViewport "scroll" catches that drift and resets it immediately.
        const handleViewportScroll = () => {
            if (isKeyboardOpen.current) {
                scrollToTop()
            }
        }

        // Scroll reset only for text inputs — avoids interfering with button/link focus.
        const handleFocusIn = (e: FocusEvent) => {
            const tag = (e.target as HTMLElement)?.tagName
            if (tag === 'INPUT' || tag === 'TEXTAREA')
                requestAnimationFrame(() => scrollToTop())
        }

        vp.addEventListener('resize', handleResize)
        vp.addEventListener('scroll', handleViewportScroll)
        document.addEventListener('focusin', handleFocusIn)

        return () => {
            vp.removeEventListener('resize', handleResize)
            vp.removeEventListener('scroll', handleViewportScroll)
            document.removeEventListener('focusin', handleFocusIn)
            closeKeyboard()
        }
    }, [])
}

export default useVirtualKeyboard
